import Image from "next/image";
import Link from "next/link";
import { canOptimizeExploreImage } from "../_lib/explore-image";
import type { ExploreStoryCard } from "../types";

interface MobileStoryCardElevatedProps {
  story: ExploreStoryCard;
  exploreBase: string;
}

/**
 * Elevated card variant — each story sits on its own card substrate
 * with background, padding, rounded corners, and subtle shadow.
 * Same text-first layout as the New Yorker style but with
 * visual separation between stories via card containers.
 */
export function MobileStoryCardElevated({
  story,
  exploreBase,
}: MobileStoryCardElevatedProps) {
  const primaryTag = story.productTags[0] ?? story.topicTags[0];
  const categoryLabel = story.categoryDisplayName;
  const shouldOptimizeImage = story.coverImageUrl
    ? canOptimizeExploreImage({ src: story.coverImageUrl })
    : false;

  return (
    <Link
      className="block overflow-hidden rounded-2xl border border-[var(--explore-card-border)] bg-[var(--explore-bg-card)] p-4 no-underline shadow-[var(--explore-card-shadow-lg)] transition-shadow active:shadow-[var(--explore-inline-shadow)]"
      href={`${exploreBase}/stories/${story.storyId}`}
      style={{ fontFamily: "var(--homepage-sans-stack)" }}
    >
      <div className="flex gap-3.5">
        {/* Text content — takes all available width */}
        <div className="flex min-w-0 flex-1 flex-col">
          {/* New indicator — top, subtle */}
          {story.isNew && (
            <div className="mb-1.5 flex items-center gap-1">
              <span className="h-1.5 w-1.5 animate-[explore-pulse-dot_1.5s_ease-in-out_infinite] rounded-full bg-[var(--explore-new-indicator)]" />
              <span className="font-bold text-[0.6rem] text-[var(--explore-new-indicator)] uppercase tracking-[0.04em]">
                New
              </span>
            </div>
          )}

          {/* Headline — always reads first */}
          <h3
            className="mb-1.5 line-clamp-4 text-pretty font-bold text-[1.25rem] text-[var(--explore-text-primary)] leading-[1.3]"
            style={{ fontFamily: "var(--explore-card-title-stack)" }}
          >
            {story.headline}
          </h3>

          {/* Summary — secondary info scent */}
          <p className="mb-2.5 line-clamp-2 text-[0.88rem] text-[var(--explore-text-secondary)] leading-[1.45]">
            {story.summary}
          </p>

          {/* Category + tag + time — small, muted, bottom */}
          <div className="flex items-center gap-1.5 text-[0.68rem] text-[var(--explore-text-muted)]">
            {categoryLabel && (
              <>
                <span className="font-medium">
                  {story.categoryEmoji && (
                    <span className="mr-0.5">{story.categoryEmoji}</span>
                  )}
                  {categoryLabel}
                </span>
                <span>·</span>
              </>
            )}
            {primaryTag && (
              <>
                <span className="font-medium">{primaryTag.displayName}</span>
                <span>·</span>
              </>
            )}
            <span>{story.timeAgo}</span>
          </div>
        </div>

        {/* Small thumbnail on right */}
        {story.coverImageUrl && (
          <div className="relative h-[76px] w-[76px] shrink-0 self-start overflow-hidden rounded-lg bg-gradient-to-br from-[var(--explore-tag-bg)] to-[var(--explore-border-light)]">
            <Image
              alt=""
              className="h-full w-full object-cover"
              fill
              sizes="76px"
              src={story.coverImageUrl}
              unoptimized={!shouldOptimizeImage}
            />
          </div>
        )}
      </div>
    </Link>
  );
}
